import { useState, useEffect, useMemo } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import fleetApi from "@/lib/fleetApi";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { downloadCSV } from "@/lib/exporters";
import { Panel, PageHeader, StatusPill, Loading } from "@/components/portal/widgets";
import { Input } from "@/components/ui/input";
import { Search, Info, ChevronLeft, ChevronRight, Download, Loader2, X, Clock, MapPinOff, Sparkles, Navigation2, History, Timer } from "lucide-react";
import { STATUS_FILTERS, statusParam, willCallWaitMin, WILL_CALL_WAIT_MIN } from "@/lib/tripStatus";
import { WillCallBadge } from "@/components/portal/TripStatusTools";
import ReassignDialog from "@/components/portal/ReassignDialog";
import AutoAssignDialog from "@/components/portal/AutoAssignDialog";
import { AskOwnerButton } from "@/components/portal/AskOwnerButton";
import TripStatusDialog from "@/components/portal/TripStatusDialog";
import TripTimeDialog from "@/components/portal/TripTimeDialog";
import { dayKey, todayKey, fmtTime, fmtDayKey } from "@/lib/time";
import { useLiveRefresh, UpdatedAgo } from "@/lib/liveRefresh";
import { RiskBadge, RISK_ORDER } from "@/components/portal/widgets";

const darkSelect = "rounded-md border border-slate-700 bg-slate-900 px-2 py-2 text-xs text-white focus:outline-none focus:ring-2 focus:ring-blue-600";
const RISKS = ["all", "on_track", "at_risk", "predicted_late", "missed"];
const ADDR = "address_issues";
const isOpenUnassigned = (t) => !t.assigned_driver_id && !t.assigned_driver_name && !/complete|cancel|no.?show|on hold/i.test(t.status || "") && !(t.status === "Will Call" && !t.will_call_ready_at);
const shiftDay = (d, n) => { const x = new Date(`${d}T12:00:00`); x.setDate(x.getDate() + n); return x.toISOString().slice(0, 10); };
const PAGE_SIZE = 25;

export default function DispatchPage() {
  const { user, organization } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const isOwnerRole = user?.role === "owner";
  const rawDate = searchParams.get("date") || "";
  const date = rawDate && (isOwnerRole || rawDate >= todayKey()) ? rawDate : "";
  const upcoming = !date && searchParams.get("day") === "upcoming";
  const status = searchParams.get("status") || "";
  useEffect(() => {
    if (rawDate && !date) { const n = new URLSearchParams(searchParams); n.delete("date"); setSearchParams(n, { replace: true }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawDate, date]);
  const [trips, setTrips] = useState(null);
  const [q, setQ] = useState(searchParams.get("q") || "");
  const isPast = !!date && date < todayKey() && !q.trim();
  const [risk, setRisk] = useState(searchParams.get("risk") || "all");
  const [page, setPage] = useState(1);
  const [reassign, setReassign] = useState(null);
  const [statusTrip, setStatusTrip] = useState(null);
  const [timeTrip, setTimeTrip] = useState(null);
  const [error, setError] = useState(false);
  const canEdit = ["owner", "supervisor", "dispatcher"].includes(user?.role);
  const isOwner = user?.role === "owner";
  const [aiOpen, setAiOpen] = useState(isOwner && ["1", "file"].includes(searchParams.get("ai")));
  const [fileIds, setFileIds] = useState(() => { if (searchParams.get("ai") !== "file") return null; try { return new Set(JSON.parse(sessionStorage.getItem("rs_ai_ids") || "[]")); } catch { return null; } });
  const [reloadKey, setReloadKey] = useState(0);
  const [sortBy, setSortBy] = useState("time");
  const updatedAt = useLiveRefresh(() => fleetApi.get(tripsUrl()).then((r) => { setTrips(r.data); setError(false); }), 20000);

  const [dq, setDq] = useState(q.trim());
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDq(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);

  const tripsUrl = () => {
    const params = new URLSearchParams();
    if (risk !== "all" && risk !== ADDR) params.set("risk", risk);
    if (statusParam(status)) params.set("status", statusParam(status));
    if (dq) params.set("q", dq);
    else if (date) params.set("date", date);
    else params.set("day", upcoming ? "upcoming" : "today");
    params.set("limit", "5000");
    return `/trips?${params.toString()}`;
  };

  useEffect(() => {
    let alive = true;
    setSearching(true);
    fleetApi.get(tripsUrl())
      .then((r) => { if (alive) { setTrips(r.data); setError(false); } })
      .catch(() => { if (alive) setTrips((prev) => { if (!prev) setError(true); return prev; }); })
      .finally(() => { if (alive) setSearching(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [risk, dq, date, upcoming, status, reloadKey, sortBy]);

  const [changedIds, setChangedIds] = useState(() => new Set());
  useEffect(() => {
    fleetApi.get("/audit").then((r) => {
      const ids = (r.data || []).filter((a) => a.entity_type === "trip" && /resched|time_change|time-change/i.test(a.action || "")).map((a) => a.entity_id);
      setChangedIds(new Set(ids));
    }).catch(() => {});
  }, []);
  const [addrFlags, setAddrFlags] = useState({});
  useEffect(() => {
    api.get("/addresses/flags").then((r) => {
      const m = {};
      (r.data.flags || []).forEach((f) => { (m[f.trip_id] = m[f.trip_id] || []).push(f); });
      setAddrFlags(m);
    }).catch(() => {});
  }, []);
  const isTimeChanged = (t) => changedIds.has(t.id) || !!(t.time_changed || t.time_locked || t.rescheduled || t.reschedule_count || t.time_change_count || t.original_pickup);
  const applyTimeChange = (tripId, patch) => {
    applyStatus(tripId, patch);
    setChangedIds((prev) => new Set(prev).add(tripId));
  };

  const setParam = (patch) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([k, v]) => { if (v) next.set(k, v); else next.delete(k); });
    setSearchParams(next);
  };
  const clearDate = () => setParam({ date: "", day: "" });
  const goDate = (d) => setParam({ date: d === todayKey() ? "" : d, day: "" });

  const filtered = useMemo(() => {
    if (!trips) return [];
    const out = trips.filter((t) => {
      if (risk === ADDR) { if (!addrFlags[t.id]) return false; } else if (risk !== "all" && t.risk !== risk) return false;
      return true;
    });
    if (sortBy === "risk") out.sort((a, b) => (RISK_ORDER[a.risk] ?? 9) - (RISK_ORDER[b.risk] ?? 9) || (b.predicted_delay_min || 0) - (a.predicted_delay_min || 0));
    return out;
  }, [trips, risk, addrFlags, sortBy]);
  const waitingLong = filtered.filter((t) => (willCallWaitMin(t) ?? -1) >= WILL_CALL_WAIT_MIN);

  useEffect(() => { setPage(1); }, [q, risk, date, upcoming, status]);

  const exportCSV = () => {
    const org = organization?.name || "RideStamp";
    downloadCSV(`ridestamp-trips-${date || todayKey()}.csv`, [{
      title: `Trips & Dispatch — ${org} (${filtered.length} trips)`,
      headers: ["Trip ID", "Rider", "Type", "Wheelchair", "Pickup", "Dropoff", "Status", "Risk", "Driver"],
      rows: filtered.map((t) => [
        t.external_trip_id, t.rider_name, t.trip_type, t.wheelchair ? "Yes" : "No",
        t.pickup_address, t.dropoff_name || t.dropoff_address, t.status, t.risk,
        t.assigned_driver_name || "Unassigned",
      ]),
    }]);
  };

  const applyReassign = (tripId, patch) => setTrips((prev) => prev.map((t) => (t.id === tripId ? { ...t, ...patch } : t)));
  const applyStatus = (tripId, patch) => setTrips((prev) => prev.map((t) => (t.id === tripId ? { ...t, ...patch } : t)));

  const tripsById = useMemo(() => Object.fromEntries((trips || []).map((t) => [t.id, t])), [trips]);
  const unassignedIds = useMemo(() => filtered.filter(isOpenUnassigned).map((t) => t.id), [filtered]);
  const openManual = (tripId) => { setAiOpen(false); setReassign(tripsById[tripId] || null); };

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * PAGE_SIZE;
  const pageRows = filtered.slice(start, start + PAGE_SIZE);

  if (error) return <div className="rounded-lg border border-red-500/30 bg-red-500/5 px-5 py-8 text-center text-sm text-red-300" data-testid="dispatch-error">Couldn't load trips from the RideStamp backend. Please refresh to try again.</div>;
  if (!trips) return <Loading />;

  return (
    <div data-testid="dispatch-page">
      <PageHeader title="Trips & Dispatch" subtitle={dq ? `${filtered.length} trips matching “${dq}” across all trips` : `${filtered.length} trip${filtered.length === 1 ? "" : "s"} ${upcoming ? "coming up after today" : date ? `on ${fmtDayKey(date)}` : "today"}${risk === ADDR ? " with address issues" : ""}`}>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 rounded-lg border border-slate-700 bg-slate-900 p-1" data-testid="dispatch-day-nav">
            <button onClick={clearDate} data-testid="dispatch-day-today" className={`rounded-md px-2.5 py-1 text-xs font-semibold ${!date && !upcoming ? "bg-blue-700 text-white" : "text-slate-300 hover:bg-slate-800"}`}>Today</button>
            <button onClick={() => setParam({ day: "upcoming", date: "" })} data-testid="dispatch-day-upcoming" className={`rounded-md px-2.5 py-1 text-xs font-semibold ${upcoming ? "bg-blue-700 text-white" : "text-slate-300 hover:bg-slate-800"}`}>Upcoming</button>
            <button onClick={() => goDate(shiftDay(date || todayKey(), -1))} disabled={!isOwnerRole && (date || todayKey()) <= todayKey()} data-testid="dispatch-day-prev" aria-label="Previous day"
              className="rounded-md p-1 text-slate-300 hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-30"><ChevronLeft className="h-4 w-4" /></button>
            <input type="date" value={date || (upcoming ? "" : todayKey())} min={isOwnerRole ? undefined : todayKey()} onChange={(e) => e.target.value && goDate(e.target.value)} data-testid="dispatch-date-picker"
              className="rounded-md bg-transparent px-1 py-0.5 text-xs text-white [color-scheme:dark] focus:outline-none" aria-label="Pick a day" />
            <button onClick={() => goDate(shiftDay(date || todayKey(), 1))} data-testid="dispatch-day-next" aria-label="Next day" className="rounded-md p-1 text-slate-300 hover:bg-slate-800"><ChevronRight className="h-4 w-4" /></button>
            {date && <button onClick={clearDate} data-testid="dispatch-date-chip" className="rounded-md p-1 text-slate-400 hover:bg-slate-800" aria-label="Back to today"><X className="h-3.5 w-3.5" /></button>}
          </div>
          <select value={status} onChange={(e) => setParam({ status: e.target.value })} data-testid="trip-status-filter" className={darkSelect} aria-label="Status">
            {STATUS_FILTERS.map(([v, l]) => <option key={v || "all"} value={v}>{l}</option>)}
          </select>
          <div className="relative">
            {searching
              ? <Loader2 className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 animate-spin text-blue-400" data-testid="trip-search-loading" />
              : <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-500" />}
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search all trips: rider, trip ID, driver…"
              data-testid="trip-search" className="w-72 border-slate-700 bg-slate-900 pl-8 text-white placeholder:text-slate-500" />
          </div>
          <select value={risk} onChange={(e) => setRisk(e.target.value)} data-testid="trip-risk-filter" className={darkSelect}>
            {RISKS.map((r) => <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
            <option value={ADDR} data-testid="trip-filter-address-issues">address issues ({Object.keys(addrFlags).length})</option>
          </select>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} data-testid="trip-sort" className={darkSelect} aria-label="Sort trips">
            <option value="time">sort: pickup time</option>
            <option value="risk">sort: most at risk</option>
          </select>
          <UpdatedAgo at={updatedAt} testid="dispatch-live-updated" />
          {isOwner && (
            <button onClick={() => setAiOpen(true)} data-testid="ai-assign-open-btn"
              className="inline-flex items-center gap-1.5 rounded-md bg-blue-700 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-blue-800">
              <Sparkles className="h-3.5 w-3.5" /> AI auto-assign ({unassignedIds.length})
            </button>
          )}
          {isOwner && (
            <button onClick={() => navigate("/portal/history")} data-testid="trip-history-open-btn"
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-xs font-medium text-slate-200 transition-colors hover:bg-slate-800">
              <History className="h-3.5 w-3.5" /> Trip History
            </button>
          )}
          {canEdit && (
            <button onClick={() => navigate("/portal/time-fixes")} data-testid="time-fixes-open-btn"
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-xs font-medium text-slate-200 transition-colors hover:bg-slate-800">
              <Clock className="h-3.5 w-3.5" /> Fix trip times
            </button>
          )}
          <button onClick={exportCSV} disabled={filtered.length === 0} data-testid="dispatch-export-csv"
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-xs font-medium text-slate-200 transition-colors hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-40">
            <Download className="h-3.5 w-3.5" /> Export CSV
          </button>
        </div>
      </PageHeader>

      {isPast && (
        <div className="mb-3 flex items-start gap-2.5 rounded-lg border border-amber-500/30 bg-amber-500/5 px-4 py-2.5 text-xs text-amber-200" data-testid="dispatch-past-note">
          <History className="mt-0.5 h-3.5 w-3.5 shrink-0" /> Past day — includes trips already filed in Day Review and History.
        </div>
      )}
      {waitingLong.length > 0 && (
        <button onClick={() => setParam({ status: "Will Call" })} data-testid="dispatch-willcall-alert"
          className="mb-3 flex w-full items-start gap-2.5 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-2.5 text-left text-xs font-semibold text-red-200 hover:bg-red-500/15">
          <Timer className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {waitingLong.length} Will Call rider{waitingLong.length === 1 ? " has" : "s have"} been waiting over {WILL_CALL_WAIT_MIN} minutes without a driver — {waitingLong.map((t) => t.rider_name).slice(0, 3).join(", ")}{waitingLong.length > 3 ? "…" : ""}
        </button>
      )}
      <div className="mb-4 flex items-start gap-2.5 rounded-lg border border-slate-800 bg-blue-500/5 px-4 py-2.5 text-xs text-slate-400" data-testid="dispatch-note">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-blue-400" />
        <span>{isOwner
          ? "Live view of all trips. Use AI auto-assign to plan drivers for unassigned trips, then review and confirm — or Assign / Reassign any trip by hand. Changes are written to the shared RideStamp backend and audited."
          : "Live view of all trips. Assigning and reassigning drivers is done by the owner — use “Ask owner to assign” on an unassigned trip to send them a request."}</span>
      </div>

      <Panel testid="trips-table">
        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#111827]">
              <tr className="border-b border-slate-800 text-left font-mono-data text-[11px] uppercase tracking-wider text-slate-500">
                <th className="px-5 py-3">Trip / Rider</th><th className="px-3 py-3">Route</th>
                <th className="px-3 py-3">Status</th><th className="px-3 py-3">Risk</th><th className="px-3 py-3">Driver</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {pageRows.map((t) => (
                <tr key={t.id} className="hover:bg-slate-800/30" data-testid={`trip-row-${t.id}`}>
                  <td className="px-5 py-3">
                    <div className="flex flex-wrap items-center gap-2 font-semibold text-white">
                      <Link to={`/portal/trips/${t.id}`} className="hover:text-blue-300 hover:underline" data-testid={`trip-open-${t.id}`}>{t.rider_name}{t.wheelchair ? " ♿" : ""}</Link>
                      <WillCallBadge trip={t} testid={`willcall-tag-${t.id}`} />
                      {addrFlags[t.id] && (
                        <button type="button" onClick={() => navigate("/portal/import")} data-testid={`address-issue-tag-${t.id}`}
                          title={addrFlags[t.id].map((f) => `${f.field === "pickup_address" ? "Pickup" : "Drop-off"}: ${f.reason}`).join("\n")}
                          className="inline-flex items-center gap-1 rounded-full border border-red-500/40 bg-red-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-red-300 hover:bg-red-500/20">
                          <MapPinOff className="h-3 w-3" /> {addrFlags[t.id].length > 1 ? "Addresses" : addrFlags[t.id][0].field === "pickup_address" ? "Pickup address" : "Drop-off address"}
                        </button>
                      )}
                      {isTimeChanged(t) && (
                        <button type="button" onClick={() => setTimeTrip(t)} data-testid={`time-changed-tag-${t.id}`} title="Pickup time was changed for this trip only — click to see who and why"
                          className="inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-300 hover:bg-amber-500/20">
                          <Clock className="h-3 w-3" /> Time changed
                        </button>
                      )}
                      {t.navigation_started_at && !/complete|cancel|no.?show/i.test(t.status || "") && (
                        <span data-testid={`navigating-tag-${t.id}`} title={`Driver started navigation ${fmtTime(t.navigation_started_at)}`}
                          className="inline-flex items-center gap-1 rounded-full border border-cyan-500/40 bg-cyan-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-cyan-300">
                          <Navigation2 className="h-3 w-3" /> Navigating{t.eta_min != null ? ` · ${t.eta_min} min` : ""}
                        </span>
                      )}
                    </div>
                    <div className="font-mono-data text-[11px] text-slate-500">{t.external_trip_id} · {t.trip_type}{t.scheduled_pickup && t.status !== "Will Call" ? ` · ${upcoming || dq ? `${fmtDayKey(dayKey(t.scheduled_pickup))} ` : ""}${fmtTime(t.scheduled_pickup)}` : t.status === "Will Call" ? " · will call" : ""}
                      {canEdit && (
                        <button onClick={() => navigate(`/portal/time-fixes?trip=${t.id}`)} data-testid={`time-btn-${t.id}`}
                          className="ml-2 rounded border border-slate-700 bg-slate-800/60 px-1.5 py-0.5 font-sans text-[11px] font-medium text-blue-300 transition-colors hover:bg-slate-700">
                          Change time
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-3 max-w-xs text-xs text-slate-400">{t.pickup_address} → {t.dropoff_name || t.dropoff_address}</td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2">
                      <StatusPill value={t.status} />
                      {canEdit && (
                        <button onClick={() => setStatusTrip(t)} data-testid={`status-btn-${t.id}`}
                          className="rounded border border-slate-700 bg-slate-800/60 px-2 py-0.5 text-[11px] font-medium text-blue-300 transition-colors hover:bg-slate-700">
                          Update
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-3"><RiskBadge trip={t} testid={`risk-badge-${t.id}`} /></td>
                  <td className="px-3 py-3" data-testid={`trip-driver-${t.id}`}>
                    <div className="flex items-center gap-2">
                      <span className="text-slate-300">{t.assigned_driver_name || <span className="text-slate-500">Unassigned</span>}</span>
                      {isOwner && t.status !== "On Hold" && (
                        <button onClick={() => setReassign(t)} data-testid={`reassign-btn-${t.id}`}
                          className="rounded border border-slate-700 bg-slate-800/60 px-2 py-0.5 text-[11px] font-medium text-blue-300 transition-colors hover:bg-slate-700">
                          {t.assigned_driver_id || t.assigned_driver_name ? "Reassign" : "Assign"}
                        </button>
                      )}
                      {!isOwner && canEdit && isOpenUnassigned(t) && <AskOwnerButton trip={t} />}
                    </div>
                  </td>
                </tr>
              ))}
              {pageRows.length === 0 && (
                <tr><td colSpan={5} className="px-5 py-10 text-center text-sm text-slate-500" data-testid="trips-empty">No trips match your filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {filtered.length > 0 && (
          <div className="flex flex-col items-center justify-between gap-3 border-t border-slate-800 px-5 py-3 text-xs text-slate-400 sm:flex-row" data-testid="dispatch-pagination">
            <span data-testid="pagination-summary">
              Showing <span className="font-semibold text-slate-200">{start + 1}–{Math.min(start + PAGE_SIZE, filtered.length)}</span> of{" "}
              <span className="font-semibold text-slate-200">{filtered.length}</span> trips
            </span>
            <div className="flex items-center gap-2">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage <= 1}
                data-testid="pagination-prev"
                className="inline-flex items-center gap-1 rounded-md border border-slate-700 bg-slate-900 px-3 py-1.5 font-medium text-slate-200 transition-colors hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-40">
                <ChevronLeft className="h-3.5 w-3.5" /> Prev
              </button>
              <span className="px-1 font-mono-data" data-testid="pagination-page">Page {safePage} / {totalPages}</span>
              <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages}
                data-testid="pagination-next"
                className="inline-flex items-center gap-1 rounded-md border border-slate-700 bg-slate-900 px-3 py-1.5 font-medium text-slate-200 transition-colors hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-40">
                Next <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        )}
      </Panel>

      <ReassignDialog trip={reassign} open={!!reassign}
        onOpenChange={(v) => { if (!v) setReassign(null); }} onReassigned={applyReassign} />
      {isOwner && (
        <AutoAssignDialog open={aiOpen} onOpenChange={(v) => { setAiOpen(v); if (!v && fileIds) { setFileIds(null); setParam({ ai: "" }); } }} tripIds={fileIds ? unassignedIds.filter((id) => fileIds.has(id)) : unassignedIds} tripsById={tripsById}
          scopeLabel={fileIds ? "from this import" : upcoming ? "coming up" : date ? `on ${fmtDayKey(date)}` : "today"} onManual={openManual} onConfirmed={() => setReloadKey((k) => k + 1)} />
      )}
      <TripStatusDialog trip={statusTrip} open={!!statusTrip}
        onOpenChange={(v) => { if (!v) setStatusTrip(null); }} onUpdated={applyStatus} />
      <TripTimeDialog trip={timeTrip} open={!!timeTrip}
        onOpenChange={(v) => { if (!v) setTimeTrip(null); }} onUpdated={applyTimeChange} />
    </div>
  );
}
